export async function withLoadTimeout<T>(
    action: (signal: AbortSignal) => Promise<T>,
    signal?: AbortSignal,
    timeoutMs = 15000,
): Promise<T> {
    signal?.throwIfAborted();
    const controller = new AbortController();
    const abort = () => controller.abort(signal?.reason);
    signal?.addEventListener("abort", abort, { once: true });
    let rejectAbort: () => void = () => {};
    const cancelled = new Promise<never>((_, reject) => {
        rejectAbort = () => reject(controller.signal.reason);
        controller.signal.addEventListener("abort", rejectAbort, { once: true });
    });
    const timer = setTimeout(() => controller.abort(new Error("Image request timed out")), timeoutMs);
    try {
        return await Promise.race([action(controller.signal), cancelled]);
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", abort);
        controller.signal.removeEventListener("abort", rejectAbort);
    }
}

export async function decodePhoto(buffer: ArrayBuffer, signal?: AbortSignal) {
    const url = URL.createObjectURL(new Blob([buffer]));
    const image = new Image();
    try {
        await withLoadTimeout(async (attemptSignal) => {
            await new Promise<void>((resolve, reject) => {
                const abort = () => finish(attemptSignal.reason);
                const finish = (error?: unknown) => {
                    image.onload = null;
                    image.onerror = null;
                    attemptSignal.removeEventListener("abort", abort);
                    if (error) reject(error);
                    else resolve();
                };
                image.onload = () => finish();
                image.onerror = () => finish(new Error("Image decode failed"));
                attemptSignal.addEventListener("abort", abort, { once: true });
                image.src = url;
                if (image.decode) image.decode().then(() => finish(), finish);
            });
            attemptSignal.throwIfAborted();
            if (!image.naturalWidth || !image.naturalHeight) throw new Error("Empty image");
        }, signal);
        return { image, url };
    } catch (error) {
        image.src = "";
        URL.revokeObjectURL(url);
        throw error;
    }
}
